import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  InputBase,
  IconButton,
  Paper,
  Popper,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ecommerceService from '../../../services/ecommerceService';

/**
 * SearchBar
 * Storefront search with debounced instant autocomplete results.
 * Submitting navigates to the product list filtered by the query.
 */
const SearchBar = ({ fullWidth = true }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await ecommerceService.instantSearch(q.trim());
      const products =
        data?.products || data?.results || data?.suggestions || [];
      setResults(Array.isArray(products) ? products.slice(0, 6) : []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const submit = (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setOpen(false);
    navigate(`/shop/products?search=${encodeURIComponent(term)}`);
  };

  const goToProduct = (p) => {
    setOpen(false);
    setQuery('');
    if (p.slug) navigate(`/shop/products/${p.slug}`);
    else submit(p.name || p.text);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ width: fullWidth ? '100%' : 'auto', position: 'relative' }} ref={anchorRef}>
        <Paper
          variant="outlined"
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.25,
            borderRadius: 999,
            bgcolor: 'grey.100',
            border: 'none',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
          <InputBase
            placeholder="Search for products, brands and more"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => query && setOpen(true)}
            sx={{ flex: 1, fontSize: '0.9rem' }}
          />
          {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
          {query && (
            <IconButton size="small" onClick={() => { setQuery(''); setResults([]); }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Paper>

        <Popper
          open={open && results.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper elevation={6} sx={{ mt: 1, borderRadius: 2, overflow: 'hidden' }}>
            <List dense disablePadding>
              {results.map((p, i) => (
                <ListItemButton key={p.id || p.slug || i} onClick={() => goToProduct(p)}>
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={p.image_url || p.image}
                      alt={p.name || p.text}
                      sx={{ bgcolor: 'grey.100' }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={p.name || p.text}
                    secondary={p.category_name || p.department}
                    primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              ))}
              <ListItemButton onClick={() => submit()} sx={{ justifyContent: 'center' }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                  See all results for "{query}"
                </Typography>
              </ListItemButton>
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SearchBar;
